import { Box, Typography } from "@material-ui/core";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import {
  ButtonWrap,
  Icon,
  IconName,
  TravelAppFTUXModal,
  useDeviceTypes,
} from "halifax";
import {
  RecentExperienceSearch,
  UserPreferenceKeyEnum,
  CLICKED_ACTIVITIES_HEADER,
  ActivitiesEntryTypeEnum,
} from "redmond";
import {
  getExperiencesAvailabilityQuery,
  getIsTravelApp,
  useGetUserMetadataQuery,
  useUpdateUserMetadataMutation,
} from "@capone/common";

import "./styles.scss";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import { fetchRecentlySearchedExperiences } from "b2b-base/src/api/v1/recently-viewed-and-searched/fetchRecentlySearchedExperiences";
import { ClientContext } from "../../App";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  PORTAL_TITLE,
  SEARCH_EXPERIENCES_TITLE,
} from "../../lang/textConstants";
import { ExperiencesSearchConnectorProps } from "./container";
import { ExperiencesSearchControl } from "./components/ExperiencesSearchControl";
import { MobileExperiencesSearchControl } from "./components/MobileExperiencesSearchControl";
import { ExperiencesInfoModal } from "./components/ExperiencesInfoModal";
import {
  INFO_MODAL_CTA_TEXT,
  SUBTITLE,
  getEarnMessageToDisplay,
} from "./textConstants";
import { getBaseActivitiesProperties } from "./reducer";
import { PATH_AVAILABILITY } from "../../utils/paths";
import {
  useExperiments,
  XMOD_V1_EXPERIMENT,
  TRAVEL_APP_GTM_EXPERIMENT,
  COMBINED_SEARCH_EXPERIMENT,
} from "../../context/experiments";
import { MobileCombinedSearch } from "../common/MobileCombinedSearch/component";
import { fetchCombinedAutocomplete } from "../../api/v0/search/fetchCombinedAutocomplete";

export interface IExperiencesSearchProps
  extends ExperiencesSearchConnectorProps,
    RouteComponentProps {}

export const ExperiencesSearch = ({
  fetchRewardsAccounts,
  largestValueAccount,
  fetchRewardsAccountsCallState,
  fetchTravelWalletDetails,
  setLocation,
  setEntryType,
  fetchTripDestinations,
  entryTypesAll,
}: IExperiencesSearchProps) => {
  const { sessionInfo } = useContext(ClientContext);
  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const history = useHistory();

  const isTravelApp = getIsTravelApp();
  const { refetch: fetchUserMetadata } = useGetUserMetadataQuery({
    keys: [UserPreferenceKeyEnum.hasSeenActivitiesFtux],
  });

  const [updateUserMetadata] = useUpdateUserMetadataMutation();

  const [experiencesInfoModalOpen, setExperiencesInfoModalOpen] =
    useState(false);

  // Fetch metadata - if not seen, open modal and immediately mark as seen
  useEffect(() => {
    let canceledRequest = false;
    let timer: number | undefined;

    fetchUserMetadata()
      .then((response) => {
        const hasSeen = response?.data?.userMetadata?.hasSeenActivitiesFtux;
        if (!hasSeen && !canceledRequest) {
          timer = window.setTimeout(() => {
            if (canceledRequest) return;
            setExperiencesInfoModalOpen(true);
            updateUserMetadata({
              userMetadata: { hasSeenActivitiesFtux: true },
            });
          }, 500);
        }
      })
      .catch(() => {
        // No-op; failing to refetch should not block the page
      });

    return () => {
      canceledRequest = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, []);

  const [recentSearches, setRecentSearches] = useState<
    RecentExperienceSearch[]
  >([]);

  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isTravelAppGTMEnabled =
    getExperimentVariant(expState.experiments, TRAVEL_APP_GTM_EXPERIMENT) ===
    AVAILABLE;

  const isCombinedSearchEnabled =
    getExperimentVariant(expState.experiments, COMBINED_SEARCH_EXPERIMENT) ===
    AVAILABLE;


  const properties = useSelector(getBaseActivitiesProperties);

  useEffect(() => {
    document.title = SEARCH_EXPERIENCES_TITLE;
    fetchRewardsAccounts(false, sessionInfo);
    setTimeout(() => window.scrollTo(0, 0), 0);

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, [fetchRewardsAccounts, sessionInfo]);

  useEffect(() => {
    fetchTravelWalletDetails();
  }, []);

  useEffect(() => {
    setEntryType(ActivitiesEntryTypeEnum.HOMEPAGE);
  }, []);

  useEffect(() => {
    fetchTripDestinations();
  }, [fetchTripDestinations]);

  // Separate useEffect to track event after rewards accounts are loaded
  useEffect(() => {
    if (properties) {
      trackEvent({
        eventName: CLICKED_ACTIVITIES_HEADER,
        properties: {
          ...properties.properties,
        },
        encryptedProperties: properties.encryptedProperties,
      });
    }
  }, [properties]);

  useEffect(() => {
    let cancelled = false;

    const getRecentSearches = async () => {
      try {
        let localStorageItem = localStorage.getItem(
          "recently_searched_experiences",
        );

        // If localStorage is empty, try fetching directly from the API
        // (the Body component may not have fetched yet on initial load)
        if (!localStorageItem || localStorageItem === "[]") {
          try {
            const resp = await fetchRecentlySearchedExperiences();
            if (cancelled) return;
            if (resp?.searches && resp.searches.length > 0) {
              localStorageItem = JSON.stringify(resp.searches);
              localStorage.setItem("recently_searched_experiences", localStorageItem);
            }
          } catch {
            // Ignore — will show empty
          }
        }

        if (localStorageItem) {
          const parsedSearches: RecentExperienceSearch[] =
            JSON.parse(localStorageItem);
          const sortedSearches = parsedSearches
            .filter((search) => !!search.location)
            // Non-destination results (attractions, categories) only work with
            // combined search routing — filter them out for non-experiment users
            .filter(
              (search) =>
                isCombinedSearchEnabled ||
                !search.suggestionType ||
                search.suggestionType === "Destination",
            )
            .sort(
              (a, b) =>
                new Date(b.searchDate).getTime() -
                new Date(a.searchDate).getTime(),
            );
          setRecentSearches(sortedSearches);

          // Enrich recently searched items missing thumbnails
          const itemsWithoutThumbnail = sortedSearches.filter(
            (s) => !s.thumbnailUrl && s.locationLabel,
          );
          if (itemsWithoutThumbnail.length > 0) {
            // Deduplicate by locationLabel
            const uniqueLabels = Array.from(
              new Set(itemsWithoutThumbnail.map((s) => s.locationLabel)),
            );
            Promise.all(
              uniqueLabels.map((label) =>
                fetchCombinedAutocomplete({ query: label })
                  .then((resp) => {
                    /* eslint-disable @typescript-eslint/no-explicit-any -- untyped combined autocomplete API response */
                    const firstSuggestion = resp.groups
                      ?.flatMap((g: any) => g.suggestions || [])
                      ?.find((s: any) => s.thumbnailUrl);
                    /* eslint-enable @typescript-eslint/no-explicit-any */
                    return firstSuggestion
                      ? { label, thumbnailUrl: firstSuggestion.thumbnailUrl }
                      : null;
                  })
                  .catch(() => null),
              ),
            ).then((results) => {
              if (cancelled) return;
              const thumbnailMap = new Map<string, string>();
              results.forEach((r) => {
                if (r) thumbnailMap.set(r.label, r.thumbnailUrl);
              });
              if (thumbnailMap.size > 0) {
                const enriched = sortedSearches.map((s) => ({
                  ...s,
                  thumbnailUrl:
                    s.thumbnailUrl || thumbnailMap.get(s.locationLabel),
                }));
                setRecentSearches(enriched);
                // Update localStorage so future loads have thumbnails
                try {
                  localStorage.setItem(
                    "recently_searched_experiences",
                    JSON.stringify(enriched),
                  );
                } catch {
                  // Ignore
                }
              }
            }).catch(() => {});
          }
        }
      } catch (e) {
        console.error(`Failed to get recent searches from local storage: ${e}`);
      }
    };

    getRecentSearches();

    window.addEventListener(
      "update_recently_searched_experiences",
      getRecentSearches,
    );

    return () => {
      cancelled = true;
      window.removeEventListener(
        "update_recently_searched_experiences",
        getRecentSearches,
      );
    };
  }, [isCombinedSearchEnabled]);

  const onRecentSearchClick = useCallback(
    async (search: RecentExperienceSearch) => {
      const isNonDestination =
        search.suggestionType &&
        search.suggestionType !== "Destination" &&
        search.location;

      let tagParam = "";

      // For non-destination recently searched (categories, attractions),
      // look up the tag ID via combined autocomplete so we can filter by it.
      if (isNonDestination) {
        try {
          const resp = await fetchCombinedAutocomplete({
            query: search.locationLabel,
            destinationId: search.location,
          });
          const allSuggestions = (resp.groups || []).flatMap(
            (g: { suggestions?: Array<{ id?: string; label?: string }> }) =>
              g.suggestions || [],
          );
          const match = allSuggestions.find(
            (s: { label?: string }) =>
              s.label?.toLowerCase() === search.locationLabel.toLowerCase(),
          );
          if (match?.id) {
            const kindSuffix =
              search.suggestionType === "Attraction" ? "_1" : "_0";
            tagParam = `&categoryTagId=${match.id}${kindSuffix}`;
          }
        } catch {
          // Navigate without filter
        }
      }

      let url = `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
        search.locationLabel,
        search.fromDate || null,
        search.untilDate || search.fromDate || null,
        undefined,
        isTravelApp.isTravelApp,
        ActivitiesEntryTypeEnum.RECENTLY_SEARCHED,
        undefined,
        undefined,
        entryTypesAll ?? [],
      )}`;

      if (isNonDestination) {
        url += `&destinationId=${encodeURIComponent(search.location)}`;

        // Store enrichment so the availability saga preserves the
        // category/attraction name in the Tysons tracking request,
        // keeping the recently searched entry distinct from the destination.
        try {
          localStorage.setItem(
            "combined_search_enrichment",
            JSON.stringify({
              label: search.locationLabel,
              thumbnailUrl: search.thumbnailUrl,
              activityCount: search.activityCount,
              suggestionType: search.suggestionType,
            }),
          );
        } catch {
          // Ignore
        }
      }
      if (tagParam) {
        url += tagParam;
      }

      setEntryType(ActivitiesEntryTypeEnum.RECENTLY_SEARCHED);
      history.push(url);
    },
    [history, isTravelApp.isTravelApp, setEntryType, entryTypesAll],
  );

  return (
    <>
      <Box
        className={clsx("experiences-search-root", {
          desktop: matchesDesktop,
          mobile: matchesMobile,
          "mobile-experiences-search-root": matchesMobile,
          xmod: isXmodRedesign,
        })}
      >
        {matchesMobile ? (
          <Box>
            {isCombinedSearchEnabled ? (
              <MobileCombinedSearch
                pageContext="landing"
                showTrips
                recentSearches={recentSearches}
                onRecentSearchClick={onRecentSearchClick}
                onClose={() => history.goBack()}
              />
            ) : (
              <MobileExperiencesSearchControl
                onSelectLocation={setLocation}
                recentSearches={recentSearches}
                onRecentSearchClick={onRecentSearchClick}
              />
            )}
            <ButtonWrap
              className="experiences-info-cta mobile"
              onClick={() => setExperiencesInfoModalOpen(true)}
            >
              <Box className="new-tag">New</Box>
              <Typography className="experiences-info-cta-text mobile">
                {INFO_MODAL_CTA_TEXT}
              </Typography>
              <Icon name={IconName.InfoCircle} />
            </ButtonWrap>
          </Box>
        ) : (
          <Box className="experiences-search-container">
            <Box className="experiences-search-titles-and-fields-wrapper">
              <Typography variant="h1" className="search-title">
                {getEarnMessageToDisplay(
                  fetchRewardsAccountsCallState,
                  largestValueAccount,
                  true,
                )}
              </Typography>
              <Typography className="search-subtitle">{SUBTITLE}</Typography>
              <ExperiencesSearchControl
                recentSearches={recentSearches}
                onRecentSearchClick={onRecentSearchClick}
              />
              <ButtonWrap
                className="experiences-info-cta"
                onClick={() => setExperiencesInfoModalOpen(true)}
              >
                <Box className="new-tag">New</Box>
                <Typography className="experiences-info-cta-text">
                  {INFO_MODAL_CTA_TEXT}
                </Typography>
                <Icon name={IconName.InfoCircle} />
              </ButtonWrap>
            </Box>
          </Box>
        )}
      </Box>
      <ExperiencesInfoModal
        isOpen={experiencesInfoModalOpen}
        setOpen={setExperiencesInfoModalOpen}
      />
      {isTravelAppGTMEnabled && (
        <TravelAppFTUXModal
          isAnyOtherFtuxOpen={experiencesInfoModalOpen}
          currentRoute={history.location.pathname}
          trackEvent={trackEvent}
        />
      )}
    </>
  );
};
